import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { notifyProduction } from '@/lib/notifications/notificationService';
import { TEMPLATE_SUBCON_DISPATCHED, TEMPLATE_SUBCON_RECEIVED, TEMPLATE_SUBCON_CLOSED } from '@/lib/notifications/templateCodes';
import {
  SubContract,
  SubContractFilters,
  CreateSubContractRequest,
  CreateBulkSubContractRequest,
  BulkSubcontractOperation,
  DispatchSubContractRequest,
  ReceiveSubContractRequest,
  CloseSubContractRequest,
  AddProgressLogRequest,
  AvailableJobCardOperation
} from '@/types/subcontracting';
import { toast } from '@/components/ui/use-toast';
import {
  subcontractingEvents,
  createSubcontractingEvent,
  SubcontractDispatchedEvent,
  SubcontractReceivedEvent,
  SubcontractClosedEvent
} from '@/lib/events/subcontractingEvents';

// Simple auth helper - replace with your actual auth implementation
const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

export const useSubcontracting = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all sub-contracts with filters
  const fetchSubContracts = useCallback(async (filters: SubContractFilters = {}) => {
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('sub_contracts')
        .select(`
          *,
          lines:sub_contract_lines(
            *,
            job_card:job_cards(id, job_card_number)
          ),
          vendor:suppliers(
            id, name, contact_person, phone, email
          ),
          delivery_challans:delivery_challans(
            id, dc_number, status, dc_date, expected_return_date, dispatch_date,
            lines:delivery_challan_lines(
              id, item_code, item_description, qty_sent, qty_received, uom
            )
          ),
          receipts:subcontract_receipts(
            id, receipt_number, status, receipt_date, qc_required
          )
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.vendor_id) {
        query = query.eq('vendor_id', filters.vendor_id);
      }
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.date_from) {
        query = query.gte('created_at', filters.date_from);
      }
      if (filters.date_to) {
        query = query.lte('created_at', filters.date_to);
      }
      if (filters.overdue_only) {
        query = query.lt('expected_return_date', new Date().toISOString().split('T')[0]);
      }
      if (filters.search_term) {
        query = query.or(`sc_number.ilike.%${filters.search_term}%,vendor_name.ilike.%${filters.search_term}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as SubContract[];
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to fetch sub-contracts: ${err.message}`,
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Generate next SC number
  const generateSCNumber = useCallback(async (): Promise<string> => {
    try {
      const currentYear = new Date().getFullYear().toString().slice(-2);
      const { data } = await supabase
        .from('sub_contracts')
        .select('sc_number')
        .like('sc_number', `SC-${currentYear}-%`)
        .order('created_at', { ascending: false })
        .limit(1);

      if (data && data.length > 0) {
        const lastNumber = parseInt(data[0].sc_number.split('-')[2]);
        const nextNumber = (lastNumber + 1).toString().padStart(4, '0');
        return `SC-${currentYear}-${nextNumber}`;
      } else {
        return `SC-${currentYear}-0001`;
      }
    } catch (err) {
      return `SC-${new Date().getFullYear().toString().slice(-2)}-0001`;
    }
  }, [supabase]);

  // Fetch available job card operations for subcontracting
  const fetchAvailableOperations = useCallback(async (): Promise<AvailableJobCardOperation[]> => {
    setLoading(true);
    try {
      // First get subcontracted work order operations
      const { data: operations, error: opsError } = await supabase
        .from('work_order_operations_with_details')
        .select('*')
        .eq('is_subcontracted', true);

      if (opsError) throw opsError;

      // Then get job cards for these operations
      const operationIds = operations?.map(op => op.id) || [];

      if (operationIds.length === 0) {
        return [];
      }

      const { data, error } = await supabase
        .from('job_cards')
        .select(`
          id,
          job_card_number,
          quantity,
          work_order_id,
          work_order_operation_id
        `)
        .in('work_order_operation_id', operationIds)
        .in('status', ['Not Started', 'In Progress']);

      if (error) throw error;

      // Transform data to AvailableJobCardOperation format
      const result: AvailableJobCardOperation[] = [];

      data?.forEach(jobCard => {
        // Find the matching operation details
        const opDetails = operations?.find(op => op.id === jobCard.work_order_operation_id);

        if (opDetails) {
          result.push({
            job_card_id: jobCard.id,
            job_card_number: jobCard.job_card_number,
            routing_operation_id: opDetails.routing_operation_id,
            operation_name: opDetails.operation_name || 'Unknown Operation',
            item_code: opDetails.component_code || 'N/A',
            item_description: opDetails.component_name || 'Unknown',
            qty_required: jobCard.quantity || 1,
            qty_already_sent: 0, // Calculate from existing sub-contracts
            qty_available: jobCard.quantity || 1, // qty_required - qty_already_sent
            uom: 'pcs'
          });
        }
      });

      return result;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to fetch available operations: ${err.message}`,
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-create sub-contract from routing operation
  const autoCreateSubContract = useCallback(async (
    routingOperationId: string,
    workOrderOperationId: string,
    operationName: string
  ): Promise<SubContract | null> => {
    setLoading(true);
    setError(null);

    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('User not authenticated');

      // 1. Fetch routing operation details
      // Note: subcontractor_id was removed from routing_operations table
      // Vendor is now selected when creating the delivery challan
      const { data: routingOp, error: routingError } = await supabase
        .from('routing_operations')
        .select(`
          id,
          operation_cost,
          routing_id,
          component_id
        `)
        .eq('id', routingOperationId)
        .single();

      if (routingError) throw routingError;
      if (!routingOp) throw new Error('Routing operation not found');

      // 2. Vendor will be assigned when creating the Delivery Challan
      // For now, create subcontract without vendor (vendor_id will be null)
      const vendorId = null;
      const vendorName = 'To be assigned';

      // 3. Fetch materials for this routing operation
      const { data: materials, error: materialsError } = await supabase
        .from('routing_component_operations')
        .select(`
          id,
          component_id,
          bom_components!inner(
            id,
            component_name,
            component_type,
            quantity,
            unit,
            drawing_number,
            material
          )
        `)
        .eq('operation_id', routingOperationId);

      if (materialsError) throw materialsError;

      // 4. Generate SC number
      const scNumber = await generateSCNumber();

      // 5. Create sub-contract
      const { data: subContract, error: scError } = await supabase
        .from('sub_contracts')
        .insert({
          sc_number: scNumber,
          vendor_id: vendorId,
          vendor_name: vendorName,
          status: 'Planned',
          expected_return_date: null, // Will be set when creating DC
          notes: `Auto-created for operation: ${operationName}`,
          created_by: user.id
        })
        .select()
        .single();

      if (scError) throw scError;

      // 6. Create sub-contract lines from materials
      const lines = materials?.map((mat: any) => ({
        sub_contract_id: subContract.id,
        routing_operation_id: workOrderOperationId, // Use work_order_operation_id, not routing_operation_id
        operation_name: operationName,
        item_code: mat.bom_components.drawing_number || mat.bom_components.component_name,
        item_description: mat.bom_components.component_name,
        qty_required: mat.bom_components.quantity || 1,
        qty_sent: 0,
        qty_received_good: 0,
        qty_received_reject: 0,
        uom: mat.bom_components.unit || 'pcs',
        unit_cost: routingOp.operation_cost || 0
      })) || [];

      if (lines.length > 0) {
        const { error: linesError } = await supabase
          .from('sub_contract_lines')
          .insert(lines);

        if (linesError) throw linesError;
      }

      toast({
        title: 'Success',
        description: `Sub-contract ${scNumber} created successfully`,
      });

      return subContract as SubContract;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to auto-create sub-contract: ${err.message}`,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [supabase, generateSCNumber]);

  // Create new sub-contract
  const createSubContract = useCallback(async (request: CreateSubContractRequest): Promise<SubContract | null> => {
    setLoading(true);
    setError(null);

    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('User not authenticated');

      const scNumber = await generateSCNumber();

      // Insert sub-contract with 'Planned' status (new workflow)
      const { data: subContract, error: scError } = await supabase
        .from('sub_contracts')
        .insert({
          sc_number: scNumber,
          vendor_id: request.vendor_id,
          vendor_name: request.vendor_name,
          status: 'Planned', // New workflow: start with Planned status
          expected_return_date: request.expected_return_date,
          notes: request.notes,
          created_by: user.id
        })
        .select()
        .single();

      if (scError) throw scError;

      // Insert sub-contract lines
      // Filter out undefined/empty routing_operation_id and convert to null
      const lines = request.lines.map(line => {
        const lineData: any = {
          ...line,
          sub_contract_id: subContract.id
        };

        // Convert empty string or undefined routing_operation_id to null
        if (!lineData.routing_operation_id || lineData.routing_operation_id === '') {
          lineData.routing_operation_id = null;
        }

        return lineData;
      });

      const { error: linesError } = await supabase
        .from('sub_contract_lines')
        .insert(lines);

      if (linesError) throw linesError;

      toast({
        title: 'Success',
        description: `Sub-contract ${scNumber} created successfully`,
      });

      return subContract as SubContract;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to create sub-contract: ${err.message}`,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [supabase, generateSCNumber]);

  // Dispatch sub-contract
  // NOTE: In the new workflow, dispatching is handled via Delivery Challan (DC)
  // This function is kept for backward compatibility but should validate DC exists
  const dispatchSubContract = useCallback(async (request: DispatchSubContractRequest): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('User not authenticated');

      // Validate that a DC exists for this subcontract (new workflow requirement)
      const { data: dcData, error: dcCheckError } = await supabase
        .from('delivery_challans')
        .select('id, dc_number')
        .eq('sub_contract_id', request.sub_contract_id)
        .single();

      if (dcCheckError || !dcData) {
        throw new Error('Cannot dispatch subcontract without creating a Delivery Challan first');
      }

      // Update sub-contract status
      const { error: scError } = await supabase
        .from('sub_contracts')
        .update({
          status: 'Dispatched',
          dispatch_date: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', request.sub_contract_id);

      if (scError) throw scError;

      // Update line quantities
      for (const line of request.lines) {
        const { error: lineError } = await supabase
          .from('sub_contract_lines')
          .update({
            qty_sent: line.qty_sent,
            updated_at: new Date().toISOString()
          })
          .eq('id', line.line_id);

        if (lineError) throw lineError;
      }

      // Emit subcontract dispatched event for cross-module integration
      await emitSubcontractDispatchedEvent(request.sub_contract_id, request);

      // Queue email notification for subcontract dispatch
      notifyProduction(
        TEMPLATE_SUBCON_DISPATCHED,
        {
          sc_number: request.sub_contract_id,
          vendor: 'Vendor',
          items: request.lines?.length ? `${request.lines.length} item(s)` : 'N/A',
          expected_return: 'N/A',
        },
        'sub_contracts',
        request.sub_contract_id
      );

      toast({
        title: 'Success',
        description: 'Sub-contract dispatched successfully',
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to dispatch sub-contract: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update subcontract status to Vendor WIP
   * Called when vendor starts working on the materials
   */
  const markAsVendorWIP = useCallback(async (sub_contract_id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase
        .from('sub_contracts')
        .update({
          status: 'Vendor WIP',
          updated_at: new Date().toISOString()
        })
        .eq('id', sub_contract_id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Subcontract marked as Vendor WIP',
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to update status: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cancel a subcontract
   * Can only cancel if status is Planned or Dispatched
   */
  const cancelSubContract = useCallback(async (sub_contract_id: string, reason?: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      // Check current status
      const { data: scData, error: checkError } = await supabase
        .from('sub_contracts')
        .select('status, sc_number')
        .eq('id', sub_contract_id)
        .single();

      if (checkError) throw checkError;

      if (!['Planned', 'Dispatched'].includes(scData.status)) {
        throw new Error('Can only cancel subcontracts in Planned or Dispatched status');
      }

      const { error } = await supabase
        .from('sub_contracts')
        .update({
          status: 'Cancelled',
          notes: reason ? `Cancelled: ${reason}` : 'Cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', sub_contract_id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Subcontract ${scData.sc_number} cancelled successfully`,
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to cancel subcontract: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Mark sub-contract as received
  const receiveSubContract = useCallback(async (request: ReceiveSubContractRequest): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('User not authenticated');

      // Update line quantities
      for (const line of request.lines) {
        const { error: lineError } = await supabase
          .from('sub_contract_lines')
          .update({
            qty_received_good: line.qty_received_good,
            qty_received_reject: line.qty_received_reject,
            updated_at: new Date().toISOString()
          })
          .eq('id', line.line_id);

        if (lineError) throw lineError;
      }

      // Update sub-contract status
      const { error: scError } = await supabase
        .from('sub_contracts')
        .update({
          status: 'Received',
          updated_at: new Date().toISOString()
        })
        .eq('id', request.sub_contract_id);

      if (scError) throw scError;

      // Emit subcontract received event for cross-module integration
      await emitSubcontractReceivedEvent(request.sub_contract_id, request);

      // Queue email notification for subcontract received
      notifyProduction(
        TEMPLATE_SUBCON_RECEIVED,
        {
          sc_number: request.sub_contract_id,
          vendor: 'Vendor',
          good_qty: request.lines?.reduce((sum: number, l: any) => sum + (l.qty_received_good || 0), 0) || 0,
          reject_qty: request.lines?.reduce((sum: number, l: any) => sum + (l.qty_received_reject || 0), 0) || 0,
        },
        'sub_contracts',
        request.sub_contract_id
      );
      
      toast({
        title: 'Success',
        description: 'Sub-contract received successfully',
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to receive sub-contract: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Close sub-contract
  /**
   * Close sub-contract and update linked work order operation status
   */
  const closeSubContract = useCallback(async (request: CloseSubContractRequest): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('User not authenticated');

      const totalCost = (request.additional_service_cost || 0) + (request.transport_cost || 0);

      const { error } = await supabase
        .from('sub_contracts')
        .update({
          status: 'Closed',
          additional_service_cost: request.additional_service_cost || 0,
          transport_cost: request.transport_cost || 0,
          total_cost: totalCost,
          updated_at: new Date().toISOString()
        })
        .eq('id', request.sub_contract_id);

      if (error) throw error;

      // Get linked work order operations from sub_contract_lines
      // Note: routing_operation_id actually references work_order_operations.id
      const { data: scLines } = await supabase
        .from('sub_contract_lines')
        .select('routing_operation_id')
        .eq('sub_contract_id', request.sub_contract_id)
        .not('routing_operation_id', 'is', null);

      if (scLines && scLines.length > 0) {
        const operationIds = scLines
          .map(line => line.routing_operation_id)
          .filter((id): id is string => id !== null);

        if (operationIds.length > 0) {
          // Update work order operations status to Completed
          const { error: woOpError } = await supabase
            .from('work_order_operations')
            .update({
              status: 'Completed',
              actual_end_time: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            })
            .in('id', operationIds);

          if (woOpError) {
            console.error('Failed to update work order operations:', woOpError);
            // Don't throw - SC close should still succeed
          } else {
            console.log(`✅ Updated ${operationIds.length} work order operation(s) to Completed`);
          }
        }
      }

      // Emit subcontract closed event for cross-module integration
      await emitSubcontractClosedEvent(request.sub_contract_id, request);

      // Queue email notification for subcontract closed
      notifyProduction(
        TEMPLATE_SUBCON_CLOSED,
        {
          sc_number: request.sub_contract_id,
          vendor: 'Vendor',
          total_cost: totalCost,
        },
        'sub_contracts',
        request.sub_contract_id
      );

      toast({
        title: 'Success',
        description: 'Sub-contract closed successfully',
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to close sub-contract: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Add progress log
  const addProgressLog = useCallback(async (request: AddProgressLogRequest): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('sub_contract_progress_logs')
        .insert({
          sub_contract_id: request.sub_contract_id,
          progress_percentage: request.progress_percentage,
          remarks: request.remarks,
          logged_by: user.id
        });

      if (error) throw error;

      // Update sub-contract status to 'Vendor WIP' on first progress entry
      const { data: existingLogs } = await supabase
        .from('sub_contract_progress_logs')
        .select('id')
        .eq('sub_contract_id', request.sub_contract_id);

      if (existingLogs && existingLogs.length === 1) {
        await supabase
          .from('sub_contracts')
          .update({ status: 'Vendor WIP' })
          .eq('id', request.sub_contract_id);
      }

      toast({
        title: 'Success',
        description: 'Progress log added successfully',
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to add progress log: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Fetch progress logs for a sub-contract
  const fetchProgressLogs = useCallback(async (subContractId: string) => {
    try {
      // Fetch progress logs with user info
      const { data, error } = await supabase
        .from('sub_contract_progress_logs')
        .select(`
          id,
          sub_contract_id,
          progress_percentage,
          remarks,
          logged_by,
          logged_at
        `)
        .eq('sub_contract_id', subContractId)
        .order('logged_at', { ascending: false });

      if (error) throw error;

      // If we have logs, fetch user info separately to avoid FK issues
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map(log => log.logged_by).filter(Boolean))];
        if (userIds.length > 0) {
          const { data: users } = await supabase
            .from('users')
            .select('id, full_name, email')
            .in('id', userIds);

          const userMap = new Map(users?.map(u => [u.id, u]) || []);

          return data.map(log => ({
            ...log,
            user: log.logged_by ? userMap.get(log.logged_by) || null : null
          }));
        }
      }

      return data || [];
    } catch (err: any) {
      console.error('Error fetching progress logs:', err);
      return [];
    }
  }, [supabase]);

  // Delete sub-contract (only in Planned status)
  const deleteSubContract = useCallback(async (subContractId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('User not authenticated');

      // Check if sub-contract can be deleted (only in Planned status)
      const { data: subContract } = await supabase
        .from('sub_contracts')
        .select('status')
        .eq('id', subContractId)
        .single();

      if (!subContract || subContract.status !== 'Planned') {
        throw new Error('Sub-contract can only be deleted in Planned status');
      }

      const { error } = await supabase
        .from('sub_contracts')
        .delete()
        .eq('id', subContractId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Sub-contract deleted successfully',
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to delete sub-contract: ${err.message}`,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Event emission methods
  const emitSubcontractDispatchedEvent = useCallback(async (subContractId: string, request: DispatchSubContractRequest) => {
    try {
      const { data: subContract } = await supabase
        .from('sub_contracts')
        .select(`
          *,
          lines:sub_contract_lines(
            *,
            job_card:job_cards(
              id,
              job_card_number,
              work_order_id,
              work_order_operation_id
            )
          )
        `)
        .eq('id', subContractId)
        .single();

      if (!subContract) return;

      const jobCardIds = subContract.lines?.map(line => line.job_card_id) || [];
      const operationIds = subContract.lines?.map(line => line.routing_operation_id).filter(Boolean) || [];
      const workOrderIds = subContract.lines?.map(line => line.job_card?.work_order_id).filter(Boolean) || [];

      // We don't have workstation_id or component info in sub_contract_lines, so we'll use what we have
      const materialItems = subContract.lines?.map(line => ({
        itemCode: line.item_code || 'UNKNOWN',
        quantity: line.qty_sent || 0,
        uom: line.uom || 'PCS'
      })) || [];

      const event = createSubcontractingEvent<SubcontractDispatchedEvent>(
        'subcon.dispatched',
        subContractId,
        subContract.sc_number,
        subContract.vendor_id,
        subContract.vendor_name,
        {
          jobCardIds,
          operationIds,
          workOrderIds,
          workstationIds: [], // Not available in sub_contract_lines
          materialItems,
          expectedReturnDate: subContract.expected_return_date
        }
      );

      await subcontractingEvents.emit(event);
      console.log('✅ Emitted subcontract dispatched event:', event.subContractNumber);
    } catch (error) {
      console.error('Error emitting dispatch event:', error);
    }
  }, []);

  const emitSubcontractReceivedEvent = useCallback(async (subContractId: string, request: ReceiveSubContractRequest) => {
    try {
      const { data: subContract } = await supabase
        .from('sub_contracts')
        .select(`
          *,
          lines:sub_contract_lines(
            *,
            job_card:job_cards(
              id,
              job_card_number
            )
          )
        `)
        .eq('id', subContractId)
        .single();

      if (!subContract) return;

      const jobCardIds = subContract.lines?.map(line => line.job_card_id) || [];
      const operationIds = subContract.lines?.map(line => line.routing_operation_id).filter(Boolean) || [];

      const receivedQuantities = subContract.lines?.map(line => ({
        jobCardId: line.job_card_id,
        qtyGood: line.qty_received_good || 0,
        qtyReject: line.qty_received_reject || 0
      })) || [];

      const completedItems = subContract.lines?.map(line => ({
        itemCode: line.item_code || 'UNKNOWN',
        quantity: line.qty_received_good || 0,
        uom: line.uom || 'PCS'
      })) || [];

      const event = createSubcontractingEvent<SubcontractReceivedEvent>(
        'subcon.received',
        subContractId,
        subContract.sc_number,
        subContract.vendor_id,
        subContract.vendor_name,
        {
          jobCardIds,
          operationIds,
          receivedQuantities,
          completedItems
        }
      );

      await subcontractingEvents.emit(event);
      console.log('✅ Emitted subcontract received event:', event.subContractNumber);
    } catch (error) {
      console.error('Error emitting receive event:', error);
    }
  }, []);

  const emitSubcontractClosedEvent = useCallback(async (subContractId: string, request: CloseSubContractRequest) => {
    try {
      const { data: subContract } = await supabase
        .from('sub_contracts')
        .select(`
          *,
          lines:sub_contract_lines(
            *,
            job_card:job_cards(
              id,
              job_card_number
            )
          )
        `)
        .eq('id', subContractId)
        .single();

      if (!subContract) return;

      const jobCardIds = subContract.lines?.map(line => line.job_card_id) || [];
      const operationIds = subContract.lines?.map(line => line.routing_operation_id).filter(Boolean) || [];
      
      const totalServiceCost = subContract.total_cost || 0;
      const additionalCosts = subContract.additional_service_cost || 0;
      const transportCost = subContract.transport_cost || 0;
      const variance = 0; // Will be calculated by costing integration

      const event = createSubcontractingEvent<SubcontractClosedEvent>(
        'subcon.closed',
        subContractId,
        subContract.sc_number,
        subContract.vendor_id,
        subContract.vendor_name,
        {
          jobCardIds,
          operationIds,
          totalServiceCost,
          additionalCosts,
          transportCost,
          variance
        }
      );

      await subcontractingEvents.emit(event);
      console.log('✅ Emitted subcontract closed event:', event.subContractNumber);
    } catch (error) {
      console.error('Error emitting close event:', error);
    }
  }, []);

  /**
   * Create a bulk subcontract from multiple work order operations
   * This consolidates multiple operations into a single subcontract for the same vendor
   */
  const createBulkSubContract = useCallback(async (
    request: CreateBulkSubContractRequest
  ): Promise<SubContract | null> => {
    setLoading(true);
    setError(null);

    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('User not authenticated');

      if (request.operations.length === 0) {
        throw new Error('At least one operation must be selected');
      }

      const scNumber = await generateSCNumber();

      // Create the sub-contract
      const { data: subContract, error: scError } = await supabase
        .from('sub_contracts')
        .insert({
          sc_number: scNumber,
          vendor_id: request.vendor_id,
          vendor_name: request.vendor_name,
          status: 'Planned',
          expected_return_date: request.expected_return_date,
          notes: request.notes || `Bulk subcontract for ${request.operations.length} operation(s)`,
          created_by: user.id
        })
        .select()
        .single();

      if (scError) throw scError;

      // Create sub-contract lines for each operation
      const lines = request.operations.map(op => ({
        sub_contract_id: subContract.id,
        routing_operation_id: op.work_order_operation_id, // Links to work_order_operations.id
        operation_name: op.operation_name,
        item_code: op.item_code || op.component_drawing_number || '',
        item_description: op.item_description || op.component_name || '',
        qty_required: op.qty_required,
        qty_sent: 0,
        qty_received_good: 0,
        qty_received_reject: 0,
        uom: 'pcs'
      }));

      const { error: linesError } = await supabase
        .from('sub_contract_lines')
        .insert(lines);

      if (linesError) throw linesError;

      toast({
        title: 'Success',
        description: `Bulk sub-contract ${scNumber} created with ${request.operations.length} operation(s)`,
      });

      return subContract as SubContract;
    } catch (err: any) {
      setError(err.message);
      toast({
        title: 'Error',
        description: `Failed to create bulk sub-contract: ${err.message}`,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [supabase, generateSCNumber]);

  return {
    loading,
    error,
    fetchSubContracts,
    fetchAvailableOperations,
    autoCreateSubContract,
    createSubContract,
    createBulkSubContract,
    dispatchSubContract,
    receiveSubContract,
    closeSubContract,
    addProgressLog,
    fetchProgressLogs,
    deleteSubContract,
    // New workflow functions
    markAsVendorWIP,
    cancelSubContract,
  };
};
